import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import OutlinedInput from "@mui/material/OutlinedInput";

import EditIcon from "../../../assets/Edit_Icon.svg";
import CloseIcon from "../../../assets/Close-Icon.svg";

import Disclaimer from "../../../components/common/Disclaimer";

import { LoansContext } from "../../../context/LoansContext";
import { RootContext } from "../../../context/RootContext";

import { getCookie, getOriginatorLabel } from "../../../utils/Common";
import { formatCurrency } from "../../../utils/v2/common";

import {
  getCriticalTests,
  patchSaveApplication,
} from "../../../services/network";

import { getTestTabResultData } from "../v1/Common";

import { ObjectType } from "../../../types/tableTypes";

type ParamType = {
  loanId: any;
  loanType: any;
  propType: any;
};

const styles = {
  select: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid white",
      color: "white",
    },
    border: "1px solid white",
    "&.Mui-focused": { color: "white", border: "1px solid white" },
    "& .MuiSelect-icon": {
      color: "white", // dropdown icon color
    },
    minHeight: "38px",
    height: "38px",
    maxHeight: "38px",
    fontSize: "14px",
    width: "100%",
    color: "white",
  },
};

export default function AssignedPartner(props: any) {
  const { t } = useTranslation();
  const { loanId, loanType } = useParams<ParamType>();

  const loanContextData: any = useContext(LoansContext);
  const RootContextData: any = useContext(RootContext);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [selectedPartner, setSelectedPartner] = useState<string>("");
  const [partnersList, setPartnersList] = useState<any>([]);

  const {
    eligiblePartners,
    loanDetails,
    setAssignedPartner,
    setBuyDownOptions,
    setCurrentSelectedInterestRate,
    setLoanDetails,
    setShowSnackBar,
    rulesResponse,
    setTemplateResponseData,
    fetchData,
  } = loanContextData;
  const {
    dynamicConfig,
    setDynamicLoader,
    dscrPartner,
    templateLabels,
    DSCROrgPartnerMapping,
    eligiblePartnerResults,
    partnersResults,
  } = RootContextData;

  const { assignedOrTakeOutPartnerId, setSnackBarMsg } = props;
  const isLATUser = getCookie("latData");

  const handleEditClick = () => {
    setIsEditing(true);
    setSelectedPartner(assignedOrTakeOutPartnerId);
  };

  const handlePartnerChange = (value: string) => {
    setSelectedPartner(value);
    setIsEditing(false);
    changePartner(value);
  };

  const updateResult = async (tpId: string) => {
    let tempResultKeys: ObjectType = {};
    let tempLoanResult: ObjectType = {};
    let loanTypeParam: string = "";

    const tempCriticalTests: ObjectType = {};
    const currentResponseData = rulesResponse[tpId];
    const tempLoanDetails = { ...loanDetails };
    const publicAPI2Headers = {
      accept: "application/json",
      "kc-realm": dynamicConfig?.ViteAppKcRealm,
    };

    if (loanType === "InvestorDSCR") {
      loanTypeParam = "30year";
    } else if (loanType === "BridgeLoan") {
      loanTypeParam = "bridge";
    }

    const loanTypeStr = loanTypeParam === "30year" ? "30Year" : "BRIDGE";
    for (const tpId of Object.keys(rulesResponse)) {
      const currentPartner: any = rulesResponse?.[tpId];
      const ruleVersion: string =
        currentPartner?.loanResult?.[loanId]?.ruleVersion?.RULE_VERSION || "";

      const criticalResponse: any = await getCriticalTests(ruleVersion);
      tempCriticalTests[tpId] = [
        ...(criticalResponse?.data?.criticalTestLoanEligibility ?? []),
      ];
    }

    tempResultKeys = Object.keys(currentResponseData?.loanResult);
    tempLoanResult = currentResponseData?.loanResult[tempResultKeys[0]];
    getTestTabResultData(
      rulesResponse,
      tempLoanDetails,
      publicAPI2Headers,
      true,
      tempLoanResult,
      loanTypeStr,
      fetchData,
      loanContextData,
      RootContextData,
      {},
      setTemplateResponseData,
      dynamicConfig,
      templateLabels,
      tpId,
      tempCriticalTests
    );
  };

  const changePartner = async (id: any) => {
    try {
      setDynamicLoader(true);
      const currentPartnerData: any = dscrPartner?.find(
        (partner: any) => partner?.id === id
      );
      const tempCurrentPartner = {
        partnerId: id,
        assignedPartner: isLATUser
          ? currentPartnerData?.programName
          : getOriginatorLabel(
              currentPartnerData?.programName,
              eligiblePartners
            ),
      };
      const loanData = {
        ...loanDetails,
        eligiblePartnerResults,
        partnersResults,
      };
      loanData.assignedPartner = tempCurrentPartner?.assignedPartner;
      loanData.takeOutPartner = tempCurrentPartner?.partnerId;
      loanData.takeoutPartnerStatus = "OVERRIDDEN";
      await patchSaveApplication(loanId, loanData);
      setAssignedPartner(tempCurrentPartner?.partnerId);
      if (
        partnersResults?.[tempCurrentPartner?.partnerId]?.loanResult?.[loanId]
          ?.buyDownOptions
      ) {
        setBuyDownOptions(
          partnersResults?.[tempCurrentPartner?.partnerId]?.loanResult?.[loanId]
            ?.buyDownOptions
        );
        const currentSelection = partnersResults?.[
          tempCurrentPartner?.partnerId
        ]?.loanResult?.[loanId]?.buyDownOptions?.find((option: any) => {
          return option?.discountPointAmount === 0;
        });
        setCurrentSelectedInterestRate({
          interestRate: `${(currentSelection?.interestRate * 100)?.toFixed(
            3
          )}%`,
          discountPoints: `${currentSelection?.discountPointPercentage?.toFixed(
            4
          )}`,
          discountPointsWithDollar: formatCurrency(
            currentSelection?.discountPointAmount
          ),
          totalBuyYield: currentSelection?.totalBuyYield,
        });
      }

      setLoanDetails(loanData);
      setSnackBarMsg("programSwitchedSuccessfully");
      setShowSnackBar(true);
      updateResult(tempCurrentPartner?.partnerId);
    } catch (err) {
      console.error(err);
    } finally {
      setDynamicLoader(false);
    }
  };

  useEffect(() => {
    const partnerKeys: any =
      eligiblePartners?.map((key: string) => ({
        value: key,
        label: DSCROrgPartnerMapping[key],
      })) || [];
    setPartnersList([...partnerKeys]);
  }, [eligiblePartners]);

  return (
    <Grid>
      <Disclaimer
        boldText={""}
        customClass="disclaimerBox"
        showClose={true}
        isRatesAsOff={false}
      >
        <span>{t("bestPricingForTheApplication")}</span>
      </Disclaimer>
      <div
        style={{
          backgroundColor: "#6750A4",
          color: "white",
          height: "6vh",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 10px",
          borderRadius: "6px",
          marginTop: "8px",
        }}
      >
        <span style={{ fontWeight: 500 }}>Program Selected:</span>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {!isEditing ? (
            <span style={{ fontWeight: 600 }}>
              {DSCROrgPartnerMapping[assignedOrTakeOutPartnerId]}
              &ensp;
              <Tooltip title={t("editAssignedPartner")}>
                <img
                  src={EditIcon}
                  style={{
                    width: "14px",
                    height: "14px",
                    cursor: "pointer",
                  }}
                  onClick={handleEditClick}
                />
              </Tooltip>
            </span>
          ) : null}
          {isEditing ? (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Select
                value={selectedPartner}
                defaultValue={selectedPartner}
                input={<OutlinedInput />}
                sx={styles.select}
                onChange={(e: any) => handlePartnerChange(e.target.value)}
              >
                <MenuItem value="none" disabled sx={{ fontSize: "12px" }}>
                  Select
                </MenuItem>
                {partnersList?.map((ele: any, idx: number) => {
                  return (
                    <MenuItem
                      key={ele.value}
                      value={ele.value}
                      sx={{ fontSize: "12px" }}
                    >
                      {t(ele.label)}
                    </MenuItem>
                  );
                })}
              </Select>
              &ensp;
              <Tooltip title={t("cancelEdit")}>
                <img
                  src={CloseIcon}
                  style={{
                    width: "24px",
                    height: "24px",
                    cursor: "pointer",
                  }}
                  alt="close-icon"
                  onClick={() => setIsEditing(false)}
                />
              </Tooltip>
            </div>
          ) : null}
        </div>
      </div>
    </Grid>
  );
}
